// ============================================================================
// STUDIO CANETTE — EN-TÊTE ET PIED DE PAGE
//
// L'habillage n'appartient à aucune page : le routeur le pose selon la route,
// et il se redessine tout seul quand la session change. C'est ce qui permet à
// la jauge de crédits de bouger à la seconde où une génération aboutit, sans
// que le studio ait à connaître l'en-tête.
//
// Deux variantes :
//   • 'public' — navigation marketing, « Se connecter », gros bouton d'essai ;
//   • 'app'    — navigation produit, jauge de crédits, menu du compte.
// Le studio, lui, demande `header:false, footer:false` : le poste de travail
// prend l'écran entier, l'en-tête est dans le dock.
//
// Les styles de l'habillage sont injectés ici. C'est délibéré : ni components.css
// (composants génériques) ni pages.css (une page à la fois) ne possèdent le
// chrome, et un en-tête cassé est le premier bug que voit l'utilisateur.
// Tout est portée sous .sc-head / .sc-foot, uniquement avec des jetons.
// ============================================================================

import { h, el, clear, on, frag } from './dom.js';
import { onSession, state, signOut, isAuthed } from './session.js';
import { themeToggleButton } from './theme.js';
import { navigate } from './router.js';
import { BRAND, FOOTER_NOTE } from '../../shared/copy.js';
import { SIGNUP_BONUS_CREDITS, credits as fmtCredits } from '../../shared/plans.js';

/** Sous ce solde, la jauge passe au rouge : il reste moins de trois photos. */
const LOW_CREDITS = 5;

let cfg = { header: true, footer: true, variant: 'public' };
let subscribed = false;
let menuOpen = false;
let panelOpen = false;

/* ------------------------------------------------------------------ *
 * Contenu de navigation                                               *
 * ------------------------------------------------------------------ */

// Les ancres pointent sur des sections de la landing : les identifiants sont un
// contrat avec src/pages/landing.js.
const NAV_PUBLIC = [
  { href: '/#fonctionnement', label: 'Comment ça marche' },
  { href: '/#formats', label: 'Formats' },
  { href: '/tarifs', label: 'Tarifs' },
  { href: '/#faq', label: 'FAQ' }
];

/*
 * LA NAVIGATION DE GAUCHE : CE QU'ON A FAIT.
 *
 * Deux entrées, et c'est tout : le tableau de bord d'abord — la question qu'on
 * se pose en arrivant est « où en étaient mes maquettes ? » — puis les rendus.
 * Ce sont des BIBLIOTHÈQUES, communes aux deux produits.
 *
 * « Compte » n'y figure plus : il vit dans le menu du nom, en haut à droite,
 * avec la facturation. Le répéter dans la barre donnait deux chemins pour une
 * page qu'on ouvre une fois par mois.
 */
const NAV_APP = [
  { href: '/app/projets', label: 'Mes projets' },
  { href: '/app/rendus', label: 'Mes rendus' },
  // L'entrée de tout projet : client, gamme, contenants (src/pages/nouveau.js).
  { href: '/app/nouveau', label: '＋ Nouveau projet' }
];

/*
 * LA BASCULE DE DROITE : DANS QUOI ON TRAVAILLE.
 *
 * SuperCanette et SuperBouteille sont deux applications cousines — même
 * coquille, deux moteurs, deux métiers. Les aligner avec « Mes projets » et
 * « Mes rendus » les faisait passer pour des rubriques du même niveau, alors
 * que ce sont des ATELIERS : on n'y va pas pour consulter, on y entre pour
 * fabriquer. D'où un groupe à part, à droite, dessiné comme un sélecteur.
 */
const NAV_STUDIOS = [
  { href: '/app/merch', label: 'Supermerch' },
  { href: '/app/carton', label: 'SuperBox' },
  { href: '/app/bouteille', label: 'SuperBouteille' },
  { href: '/app', label: 'SuperCanette' }
];

const FOOTER_COLS = [
  {
    title: 'Produit',
    links: [
      { href: '/#fonctionnement', label: 'Comment ça marche' },
      { href: '/#formats', label: 'Les 8 formats' },
      { href: '/tarifs', label: 'Tarifs' },
      { href: '/app', label: 'Ouvrir le studio' }
    ]
  },
  {
    title: 'Ressources',
    links: [
      { href: '/#faq', label: 'Questions fréquentes' },
      { href: '/#preuve', label: 'Ce qui est modélisé' },
      { href: `mailto:bonjour@${BRAND.domain}`, label: 'Nous écrire' }
    ]
  },
  {
    title: 'Légal',
    links: [
      { href: '/cgu', label: "Conditions d'utilisation" },
      { href: '/confidentialite', label: 'Confidentialité' },
      { href: '/mentions', label: 'Mentions légales' }
    ]
  }
];

/* ------------------------------------------------------------------ *
 * Pièces réutilisées                                                  *
 * ------------------------------------------------------------------ */

const LOGO_SVG = `<svg class="sc-logo" viewBox="0 0 22 28" width="19" height="24" aria-hidden="true">
<path d="M4.6 6.4c0-1.7 1-2.4 1.6-3V2h9.6v1.4c.6.6 1.6 1.3 1.6 3v15.2c0 1.7-1 2.4-1.6 3V26H6.2v-1.4c-.6-.6-1.6-1.3-1.6-3z"/>
<path d="M6.2 3.4h9.6" class="sc-logo__ring"/></svg>`;

const BURGER_SVG = `<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
<path d="M3.5 6.5h17M3.5 12h17M3.5 17.5h17"/></svg>`;

const CLOSE_SVG = `<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
<path d="M5.5 5.5l13 13M18.5 5.5l-13 13"/></svg>`;

function brandLink(href) {
  return h('a.sc-brand', { href, 'aria-label': `${BRAND.name} — accueil` },
    frag(LOGO_SVG),
    h('span', { text: BRAND.name })
  );
}

function navLinks(items, extraClass = '') {
  const here = location.pathname.replace(/\/+$/, '') || '/';
  return items.map(({ href, label }) => {
    const target = href.split('#')[0].replace(/\/+$/, '') || '/';
    const active = href.startsWith('/') && !href.includes('#') && target === here;
    return h('a', {
      class: extraClass, href, text: label,
      ...(active ? { 'aria-current': 'page' } : {})
    });
  });
}

/**
 * Le sélecteur d'atelier — deux liens collés, celui de la page en cours rempli.
 *
 * Un `<nav>` et non une liste de boutons : ce sont deux adresses, elles
 * s'ouvrent dans un nouvel onglet au clic du milieu comme n'importe quel lien.
 */
function studioSwitch() {
  const ici = location.pathname.replace(/\/+$/, '') || '/';
  return h('nav.sc-studios', { 'aria-label': 'Atelier' },
    ...NAV_STUDIOS.map(({ href, label }) => h('a', {
      href, text: label,
      ...(href === ici ? { 'aria-current': 'page' } : {})
    }))
  );
}

/**
 * `displayName` vient de /api/me, `name` de l'enregistrement PocketBase. On lit
 * les deux : l'en-tête doit afficher le bon nom dès la première frame, avant
 * même que /api/me n'ait répondu, sinon le libellé change sous les yeux.
 */
function displayName() {
  const u = state.user;
  if (!u) return '';
  return u.displayName || u.name || u.email || '';
}

function initials() {
  const n = displayName();
  const parts = n.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  const raw = parts.length >= 2 ? parts[0][0] + parts[1][0] : (parts[0] || '?').slice(0, 2);
  return raw.toUpperCase();
}

/** La jauge de crédits : chiffre + barre, cliquable, rouge quand ça se vide. */
function creditsGauge() {
  const { balance, monthly, unlimited } = state.credits;

  // Compte d'exploitation : pas de décompte, donc pas de jauge. Une barre
  // pleine et figée se lirait comme un solde bloqué ; le symbole ne laisse
  // aucun doute.
  if (unlimited) {
    return h('a.sc-credits.is-unlimited', {
      href: '/compte#credits',
      title: 'Compte d\'exploitation — générations illimitées',
      'aria-label': 'Crédits illimités — voir le détail'
    },
      h('span.sc-credits__n', { text: '∞' })
    );
  }

  // Le plan Essai n'a pas d'allocation mensuelle : la dotation d'inscription
  // sert de référence, sinon la barre resterait vide dès la première image.
  const ref = monthly || Math.max(balance, SIGNUP_BONUS_CREDITS);
  const pct = Math.max(0, Math.min(100, Math.round((balance / ref) * 100)));
  const low = balance < LOW_CREDITS;

  return h('a.sc-credits', {
    href: '/compte#credits',
    class: low ? 'is-low' : '',
    title: monthly
      ? `${fmtCredits(balance)} crédits restants sur ${fmtCredits(monthly)} par mois`
      : `${fmtCredits(balance)} crédits restants`,
    'aria-label': `${fmtCredits(balance)} crédits restants — voir le détail`
  },
    h('span.sc-credits__n', { text: fmtCredits(balance) }),
    h('span.meter', { style: { '--v': pct + '%' }, 'aria-hidden': 'true' },
      h('i', { style: { width: pct + '%' } })
    )
  );
}

/** Menu du compte. Bouton + panneau, fermeture au clic dehors et à Échap. */
function userMenu() {
  const btn = h('button.sc-avatar.avatar', {
    type: 'button',
    'aria-haspopup': 'menu',
    'aria-expanded': String(menuOpen),
    'aria-label': 'Menu du compte',
    title: displayName(),
    text: initials(),
    onclick: e => { e.stopPropagation(); toggleMenu(); }
  });

  const menu = h('div.sc-menu', { role: 'menu', hidden: !menuOpen },
    h('p.sc-menu__who', { text: displayName() }),
    h('a', { role: 'menuitem', href: '/compte', text: 'Compte' }),
    h('a', { role: 'menuitem', href: '/compte#facturation', text: 'Facturation' }),
    h('a', { role: 'menuitem', href: '/app/projets', text: 'Mes projets' }),
    h('a', { role: 'menuitem', href: '/app/rendus', text: 'Mes rendus' }),
    h('button.sc-menu__out', {
      type: 'button', role: 'menuitem', text: 'Se déconnecter',
      onclick: async () => { menuOpen = false; await signOut(); navigate('/'); }
    })
  );

  return h('div.sc-user', btn, menu);
}

function toggleMenu(force) {
  menuOpen = force === undefined ? !menuOpen : force;
  const bar = document.getElementById('topbar');
  const menu = el('.sc-menu', bar || document);
  const btn = el('.sc-avatar', bar || document);
  if (menu) menu.hidden = !menuOpen;
  if (btn) btn.setAttribute('aria-expanded', String(menuOpen));
}

function togglePanel(force) {
  panelOpen = force === undefined ? !panelOpen : force;
  const bar = document.getElementById('topbar');
  const panel = el('.sc-panel', bar || document);
  const btn = el('.sc-burger', bar || document);
  if (panel) panel.hidden = !panelOpen;
  if (btn) {
    btn.setAttribute('aria-expanded', String(panelOpen));
    btn.replaceChildren(frag(panelOpen ? CLOSE_SVG : BURGER_SVG));
  }
}

/* ------------------------------------------------------------------ *
 * En-tête                                                            *
 * ------------------------------------------------------------------ */

/** Désabonnement de la cloche actuellement montée — voir drawHeader(). */
let clocheOff = null;

function drawHeader() {
  const bar = document.getElementById('topbar');
  if (!bar) return;

  if (!cfg.header) { bar.hidden = true; bar.className = ''; clear(bar); return; }

  // Les deux panneaux sont refermés AVANT la reconstruction : sinon l'en-tête
  // se redessine avec l'ancien état ouvert et le drapeau, remis à zéro ensuite,
  // ne correspond plus à ce qui est affiché.
  menuOpen = false;
  panelOpen = false;

  const app = cfg.variant === 'app';
  const authed = isAuthed();
  const items = app && authed ? NAV_APP : NAV_PUBLIC;

  clear(bar);
  bar.hidden = false;
  bar.className = 'sc-head' + (app ? ' sc-head--app' : '');

  // L'en-tête se reconstruit à chaque navigation : la cloche précédente doit
  // rendre son abonnement à la file, sinon chaque page en laisserait un derrière
  // elle et le panneau se repeindrait autant de fois qu'on a navigué.
  clocheOff?.();
  clocheOff = null;

  // Les actions diffèrent selon l'état de connexion, PAS selon la variante :
  // un abonné qui relit la landing doit voir son solde, pas « Se connecter ».
  const hoteCloche = authed ? h('div.sc-head__bell') : null;
  // Dans l'application : l'atelier courant en tête des actions, puis les trois
  // témoins du compte (notifications, crédits, nom). Le sélecteur est à droite
  // parce qu'il dit OÙ L'ON TRAVAILLE, pas ce qu'on consulte.
  const actions = authed
    ? [app ? studioSwitch() : null, hoteCloche, creditsGauge(), themeToggleButton(), userMenu()]
    : [
        themeToggleButton(),
        h('a.btn.btn--ghost.btn--sm.sc-wide', { href: '/connexion', text: 'Se connecter' }),
        h('a.btn.btn--primary.btn--sm', {
          href: '/inscription',
          text: `Essayer — ${SIGNUP_BONUS_CREDITS} images offertes`
        })
      ];

  const burger = h('button.sc-burger', {
    type: 'button',
    'aria-label': 'Ouvrir le menu',
    'aria-expanded': 'false',
    onclick: e => { e.stopPropagation(); togglePanel(); }
  }, frag(BURGER_SVG));

  bar.append(
    h('div.sc-head__in',
      // Connecté, le logo ramène au tableau de bord et non dans un studio :
      // c'est la page d'où l'on repart.
      brandLink(app && authed ? '/app/projets' : '/'),
      h('nav.sc-nav', { 'aria-label': 'Navigation principale' }, ...navLinks(items)),
      h('div.sc-head__act', ...actions),
      burger
    ),
    // Panneau mobile : mêmes liens, bordure épaisse, en dessous de la barre.
    h('div.sc-panel', { hidden: true },
      h('nav.sc-panel__nav', { 'aria-label': 'Navigation mobile' },
        ...navLinks(items),
        // Le sélecteur d'atelier ne tient pas dans la barre étroite : il
        // rejoint le panneau, sous les bibliothèques, comme deux entrées de
        // plus. Sans ça, un téléphone ne pourrait plus changer de studio.
        ...(app && authed ? navLinks(NAV_STUDIOS) : [])),
      authed
        ? h('div.sc-panel__act',
            h('a.btn.btn--ghost.btn--block', { href: '/compte#credits', text: `${fmtCredits(state.credits.balance)} crédits — recharger` }),
            h('button.btn.btn--danger.btn--block', {
              type: 'button', text: 'Se déconnecter',
              onclick: async () => { togglePanel(false); await signOut(); navigate('/'); }
            })
          )
        : h('div.sc-panel__act',
            h('a.btn.btn--ghost.btn--block', { href: '/connexion', text: 'Se connecter' }),
            h('a.btn.btn--primary.btn--block', { href: '/inscription', text: `Essayer — ${SIGNUP_BONUS_CREDITS} images offertes` })
          )
    )
  );

  // Montée APRÈS l'insertion dans le document : la cloche pose ses écouteurs et
  // mesure sa position, deux choses qui n'ont pas de sens sur un nœud détaché.
  // Import différé — c'est ce qui empêche l'en-tête de toutes les pages de
  // tirer la file d'attente, et par elle la couche de génération, avant qu'un
  // utilisateur connecté n'en ait besoin.
  if (hoteCloche) {
    import('./genbell.js')
      .then(({ monterCloche }) => {
        // La navigation a pu enchaîner pendant l'import : on ne monte que si ce
        // nœud-là est toujours celui qui est à l'écran.
        if (!hoteCloche.isConnected) return;
        clocheOff = monterCloche(hoteCloche).dispose;
      })
      .catch(e => console.warn('[layout] cloche indisponible', e?.message || e));
  }
}

/* ------------------------------------------------------------------ *
 * Pied de page                                                        *
 * ------------------------------------------------------------------ */

function drawFooter() {
  const bar = document.getElementById('botbar');
  if (!bar) return;

  if (!cfg.footer) { bar.hidden = true; bar.className = ''; clear(bar); return; }

  clear(bar);
  bar.hidden = false;
  bar.className = 'sc-foot';

  bar.append(
    h('div.sc-foot__in',
      h('div.sc-foot__brand',
        brandLink('/'),
        h('p.sc-foot__base', { text: BRAND.baseline }),
        h('p.sc-foot__note', { text: FOOTER_NOTE })
      ),
      ...FOOTER_COLS.map(col =>
        h('div.sc-foot__col',
          h('h3', { text: col.title }),
          h('ul', ...col.links.map(l => h('li', h('a', { href: l.href, text: l.label }))))
        )
      )
    ),
    h('div.sc-foot__bar',
      h('span', { text: `© ${new Date().getFullYear()} ${BRAND.name}` }),
      h('span', { text: `Un outil ${BRAND.maker}` })
    )
  );
}

/* ------------------------------------------------------------------ *
 * API publique                                                        *
 * ------------------------------------------------------------------ */

/**
 * Pose l'habillage. Appelé par le routeur à chaque navigation, avec ce que la
 * page exporte dans `chrome`.
 * @param {{header?:boolean, footer?:boolean, variant?:'public'|'app'}} [opts]
 */
export function mountChrome({ header = true, footer = true, variant = 'public' } = {}) {
  ensureStyles();
  cfg = { header, footer, variant };

  if (!subscribed) {
    subscribed = true;
    // L'en-tête suit la session : connexion, déconnexion, solde de crédits.
    // Le pied de page est statique, on ne le redessine pas pour rien.
    onSession(() => { if (cfg.header) drawHeader(); });

    // Fermetures globales, posées une seule fois.
    on(document, 'click', e => {
      if (menuOpen && !e.target?.closest?.('.sc-user')) toggleMenu(false);
      if (panelOpen && !e.target?.closest?.('.sc-panel') && !e.target?.closest?.('.sc-burger')) togglePanel(false);
    });
    on(document, 'keydown', e => {
      if (e.key !== 'Escape') return;
      if (menuOpen) toggleMenu(false);
      if (panelOpen) togglePanel(false);
    });
  }

  drawHeader();
  drawFooter();
}

/** Redessine l'en-tête à la demande (solde, avatar, lien actif). */
export function refreshChrome() {
  if (cfg.header) drawHeader();
}

/* ------------------------------------------------------------------ *
 * Styles de l'habillage — jetons uniquement                           *
 * ------------------------------------------------------------------ */

function ensureStyles() {
  if (document.getElementById('sc-chrome-css')) return;
  document.head.appendChild(h('style#sc-chrome-css', {
    text: `
/* ── En-tête ─────────────────────────────────────────────────────────── */
.sc-head{position:sticky;top:0;z-index:var(--z-header);background:var(--bg);
  border-bottom:var(--bd-lg)}
.sc-head__in{display:flex;align-items:center;gap:var(--s4);
  max-width:var(--maxw);margin:0 auto;padding:var(--chrome-pad) var(--s5)}
.sc-head--app .sc-head__in{max-width:none;padding-inline:var(--s5)}

.sc-brand{display:inline-flex;align-items:center;gap:var(--s2);text-decoration:none;
  color:var(--ink);font-family:var(--font-display);font-size:var(--fs-md);
  letter-spacing:-.02em;white-space:nowrap}
.sc-brand:hover{color:var(--ink)}
.sc-logo{overflow:visible;flex:none}
.sc-logo path{fill:var(--acc);stroke:var(--ink);stroke-width:2;stroke-linejoin:round}
.sc-logo .sc-logo__ring{fill:none}

.sc-nav{display:flex;align-items:center;gap:var(--s1);margin-inline-start:var(--s3);min-width:0}
/* Les onglets sont des commandes de bandeau comme les autres : meme hauteur
   que le selecteur d'atelier, la cloche, la jauge et l'avatar. Leur hauteur
   venait de leur texte, ce qui les faisait depasser de quatre pixels — assez
   pour qu'on voie deux lignes d'assise sur une barre qui n'en a qu'une. */
.sc-nav a{display:inline-flex;align-items:center;height:var(--chrome-h);box-sizing:border-box;
  padding-inline:var(--s3);border:var(--bd-w) solid transparent;border-radius:var(--r);
  color:var(--ink);text-decoration:none;font-size:var(--fs-sm);font-weight:600;white-space:nowrap;
  transition:transform var(--t-fast) var(--ease),box-shadow var(--t-fast) var(--ease)}
.sc-nav a:hover{border-color:var(--ink);background:var(--surface);box-shadow:var(--sh-xs);
  transform:translate(var(--lift),var(--lift))}
.sc-nav a:active{transform:translate(var(--press),var(--press));box-shadow:none}
.sc-nav a[aria-current="page"]{border-color:var(--ink);background:var(--acc);box-shadow:var(--sh-xs)}
.sc-nav a:focus-visible{outline:var(--bd-w-lg) solid var(--focus);outline-offset:2px}

.sc-head__act{display:flex;align-items:center;gap:var(--s2);margin-inline-start:auto}

/* ── Les trois témoins du compte, à la même hauteur ──────────────────
   La cloche mesurait 64 px, l'avatar 36, la jauge de crédits se laissait
   dimensionner par son texte : trois rectangles posés côte à côte, trois
   hauteurs. Une seule variable les aligne, et la barre retrouve une ligne
   d'assise. Cette variable est maintenant un jeton global (tokens.css) que
   la barre de l'atelier lit AUSSI : passer d'une page a l'autre ne doit pas
   changer la taille des memes boutons.
   (Pas d'accent grave dans ce bloc : il vit dans un littéral de gabarit, et
   un seul le refermerait au milieu de la feuille.) */
.sc-head .gb__btn,
.sc-head .sc-avatar,
.sc-head .sc-credits,
.sc-head .sc-iconbtn{height:var(--chrome-h);box-sizing:border-box}
.sc-head .gb__btn,
.sc-head .sc-avatar,
.sc-head .sc-iconbtn{width:var(--chrome-h)}

/* ── Le sélecteur d'atelier ──────────────────────────────────────────
   Deux liens soudés en un seul bloc, à la hauteur des témoins voisins :
   c'est un interrupteur à deux positions, pas deux boutons indépendants. */
.sc-studios{display:flex;align-items:stretch;height:var(--chrome-h);
  overflow:hidden;border:var(--bd);border-radius:var(--r);box-shadow:var(--sh-xs)}
.sc-studios a{display:inline-flex;align-items:center;padding:0 var(--s3);
  font-size:var(--fs-xs);font-weight:700;color:var(--ink);text-decoration:none;
  background:var(--surface);border-inline-end:var(--bd-w) solid var(--ink);
  white-space:nowrap}
.sc-studios a:last-child{border-inline-end:0}
.sc-studios a:hover{background:var(--acc)}
.sc-studios a[aria-current="page"]{color:var(--bg);background:var(--ink)}
.sc-studios a:focus-visible{outline:var(--bd-w-lg) solid var(--focus);outline-offset:-3px}

/* ── Jauge de crédits ────────────────────────────────────────────────── */
.sc-credits{display:inline-flex;align-items:center;gap:var(--s2);
  padding:var(--s1) var(--s3);border:var(--bd);border-radius:var(--r);
  background:var(--surface);box-shadow:var(--sh-xs);color:var(--ink);text-decoration:none;
  transition:transform var(--t-fast) var(--ease),box-shadow var(--t-fast) var(--ease)}
.sc-credits:hover{transform:translate(var(--lift),var(--lift));box-shadow:var(--sh-sm)}
.sc-credits:active{transform:translate(var(--press),var(--press));box-shadow:none}
.sc-credits:focus-visible{outline:var(--bd-w-lg) solid var(--focus);outline-offset:2px}
.sc-credits__n{font-family:var(--font-display);font-size:var(--fs-sm);line-height:1;
  font-variant-numeric:tabular-nums}
.sc-head .meter{display:block;width:64px;height:9px;padding:0;overflow:hidden;
  border:var(--bd-w) solid var(--ink);border-radius:var(--r-pill);background:var(--alu-1)}
.sc-head .meter>i{display:block;height:100%;width:var(--v,0%);background:var(--lime)}
.sc-credits.is-low .sc-credits__n{color:var(--red)}
.sc-credits.is-low .meter>i{background:var(--red)}

/* ── Menu du compte ──────────────────────────────────────────────────── */
.sc-user{position:relative;display:inline-flex}
.sc-avatar{display:inline-flex;align-items:center;justify-content:center;
  width:36px;height:36px;padding:0;cursor:pointer;
  font-family:var(--font-display);font-size:var(--fs-xs);letter-spacing:0;
  color:var(--accent-ink);background:var(--acc);border:var(--bd);border-radius:var(--r);
  box-shadow:var(--sh-xs);
  transition:transform var(--t-fast) var(--ease),box-shadow var(--t-fast) var(--ease)}
.sc-avatar:hover{transform:translate(var(--lift),var(--lift));box-shadow:var(--sh-sm)}
.sc-avatar:active{transform:translate(var(--press),var(--press));box-shadow:none}
.sc-avatar:focus-visible{outline:var(--bd-w-lg) solid var(--focus);outline-offset:2px}
.sc-menu{position:absolute;inset-inline-end:0;top:calc(100% + var(--s2));
  min-width:216px;display:grid;gap:var(--s1);padding:var(--s2);
  background:var(--surface);border:var(--bd);border-radius:var(--r);box-shadow:var(--sh);
  z-index:var(--z-overlay)}
.sc-menu[hidden]{display:none}
.sc-menu__who{margin:0;padding:var(--s2) var(--s3);font-size:var(--fs-xs);color:var(--ink-mute);
  border-bottom:var(--bd-w) solid var(--ink);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.sc-menu a,.sc-menu button{display:block;width:100%;text-align:start;
  padding:var(--s2) var(--s3);border:var(--bd-w) solid transparent;border-radius:var(--r-xs);
  background:none;color:var(--ink);text-decoration:none;font:inherit;font-size:var(--fs-sm);
  font-weight:600;cursor:pointer}
.sc-menu a:hover,.sc-menu button:hover{background:var(--acc);border-color:var(--ink)}
.sc-menu__out{color:var(--red)!important}
.sc-menu__out:hover{background:var(--red)!important;color:var(--surface)!important}

/* ── Mobile ──────────────────────────────────────────────────────────── */
.sc-burger{display:none;align-items:center;justify-content:center;
  width:38px;height:38px;padding:0;cursor:pointer;color:var(--ink);
  background:var(--surface);border:var(--bd);border-radius:var(--r);box-shadow:var(--sh-xs)}
.sc-burger svg{fill:none;stroke:currentColor;stroke-width:2.2;stroke-linecap:round}
.sc-burger:active{transform:translate(var(--press),var(--press));box-shadow:none}
.sc-panel{display:none;border-top:var(--bd-lg);background:var(--surface);
  padding:var(--s4) var(--s5) var(--s5);box-shadow:var(--sh)}
.sc-panel__nav{display:grid;gap:var(--s1);margin-bottom:var(--s4)}
.sc-panel__nav a{padding:var(--s3);border:var(--bd);border-radius:var(--r);
  background:var(--bg);color:var(--ink);text-decoration:none;font-weight:700;
  box-shadow:var(--sh-xs)}
.sc-panel__nav a[aria-current="page"]{background:var(--acc)}
.sc-panel__act{display:grid;gap:var(--s2)}

@media (max-width:940px){
  .sc-head .sc-nav{display:none}
  .sc-head .sc-studios{display:none}
  .sc-head .sc-wide{display:none}
  .sc-burger{display:inline-flex}
  .sc-panel:not([hidden]){display:block}
  .sc-head__in{gap:var(--s3);padding-inline:var(--s4)}
}
@media (max-width:560px){
  .sc-brand span{display:none}
  .sc-head__act .btn{font-size:var(--fs-xs)}
}

/* ── Pied de page ────────────────────────────────────────────────────── */
.sc-foot{margin-top:var(--s9);border-top:var(--bd-lg);background:var(--bg-alt)}
.sc-foot__in{display:grid;gap:var(--s6);grid-template-columns:1.5fr repeat(3,1fr);
  max-width:var(--maxw);margin:0 auto;padding:var(--s7) var(--s5) var(--s6)}
.sc-foot__base{margin:var(--s3) 0 0;font-family:var(--font-display);font-size:var(--fs-md);
  letter-spacing:-.02em;max-width:22ch}
.sc-foot__note{margin:var(--s3) 0 0;font-size:var(--fs-sm);color:var(--ink-soft);max-width:38ch}
.sc-foot__col h3{margin:0 0 var(--s3);font-family:var(--font-display);font-size:var(--fs-xs);
  text-transform:uppercase;letter-spacing:.1em}
.sc-foot__col ul{list-style:none;margin:0;padding:0;display:grid;gap:var(--s2)}
.sc-foot__col a{color:var(--ink-soft);text-decoration:none;font-size:var(--fs-sm)}
.sc-foot__col a:hover{color:var(--ink);text-decoration:underline;text-underline-offset:3px}
.sc-foot__bar{display:flex;flex-wrap:wrap;gap:var(--s3);justify-content:space-between;
  max-width:var(--maxw);margin:0 auto;padding:var(--s4) var(--s5);
  border-top:var(--bd-w) solid var(--ink);font-size:var(--fs-xs);color:var(--ink-mute)}
@media (max-width:860px){.sc-foot__in{grid-template-columns:1fr 1fr}}
@media (max-width:520px){.sc-foot__in{grid-template-columns:1fr;padding-inline:var(--s4)}}
`
  }));
}
